import React, { useEffect, useState, useRef } from 'react';
import { Dialog } from '@headlessui/react';
import classNames from 'classnames';

export default function Modal({
  children,
  isOpen = false,
  onClose = () => {},
  className = '',
  title = '',
  header = true,
  modalPanelClassNames = '',
  headerDivider = true,
  closeButton = true,
}) {
  const [modalIndex, setModalIndex] = useState(1000); // Index for z-index
  const modalRef = useRef(null); // used to keep a reference of this component in the DOM

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        const openModals =
          document.getElementsByClassName('dynamic-modal open');
        const topModal = openModals[openModals.length - 1];
        if (topModal === modalRef.current) {
          // modalRef.current is a reference to the current component in the DOM
          onClose();
        }
      }
    };

    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
    } else {
      window.removeEventListener('keydown', handleKeyDown);
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    // Function to calculate and set z-index based on the number of modals open
    const calculateZIndex = () => {
      const openModals = document.getElementsByClassName('dynamic-modal open');

      // find out z-index of each open modal and find the highest z index
      let maxZIndex = 1000; // Default z-index

      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < openModals.length; i++) {
        const zIndex = parseInt(
          window.getComputedStyle(openModals[i]).zIndex,
          10,
        );
        if (zIndex > maxZIndex) {
          maxZIndex = zIndex;
        }
      }

      setModalIndex(maxZIndex + 1);
    };

    if (isOpen) {
      calculateZIndex();
      window.addEventListener('resize', calculateZIndex);
    } else {
      window.removeEventListener('resize', calculateZIndex);
    }

    return () => {
      window.removeEventListener('resize', calculateZIndex);
    };
  }, [isOpen]);

  return (
    <Dialog
      title={title}
      open={isOpen}
      onClose={() => {
        // empty call needed to override the default on close
      }}
      as="div"
      className={`dialog dynamic-modal ${isOpen ? 'open' : 'close'}`}
      ref={modalRef}
      style={{ zIndex: modalIndex }}
    >
      <div className="backdrop" aria-hidden="true" />
      <div
        className="dialog-container"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        style={{ zIndex: modalIndex }}
      >
        <Dialog.Panel
          className={classNames(
            `dialog-panel ${className} ${modalPanelClassNames} overflow-auto max-h-[90vh]`,
          )}
          style={{ zIndex: modalIndex }}
        >
          {header && (
            <div
              className="dialog-panel-header"
              style={{ borderBottom: headerDivider ? '1px solid #D8E4FA' : '' }}
            >
              <p className="dialog-panel-header-title">{title}</p>
              {closeButton && (
                <button
                  className="dialog-panel-header-action"
                  onClick={() => onClose(false)}
                  aria-label="Close dialog"
                >
                  <i className="icon-Cancel" />
                </button>
              )}
            </div>
          )}
          {children}
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}
